import { CommentsCount } from "@/components/comments/CommentsCount.tsx";
import { DataBars, DataBarsRow } from "@/components/data-bars/DataBars";
import { FormAggregatedResultElementProps } from "@/components/form-result/aggregated-result/components/types.ts";
import { Box, Stack, Text, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const FormAggregatedResultScaleElement = ({
  element,
  result,
  showDetailModalCallback,
}: FormAggregatedResultElementProps) => {
  const scaleSettings = element.scale_settings;
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!scaleSettings || !result || !result.scale_data) return null;
    const scaleFirst = scaleSettings.form_scale.scale.sort((a, b) => a.sort - b.sort);

    let sum = 0;
    for (const option of scaleFirst) {
      const selectOption = result.scale_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      sum += selectOption?.count || 0;
    }

    const rowsInner: DataBarsRow[] = [];
    for (const option of scaleFirst) {
      const selectOption = result.scale_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      const count = selectOption?.count || 0;
      const percent = sum > 0 ? Math.round((100 * count) / sum) : 0;
      rowsInner.push({
        title: option.title,
        emoji: option.emoji,
        value: count > 0 ? `${percent}%` : t("common:no_votes"),
        percent: percent,
        tooltip: (
          <Stack className={"text-sm"}>
            {count > 0 ? (
              <Text>{t("plurals:responses_count", { count: count })}</Text>
            ) : (
              <Text>{t("common:no_votes")}</Text>
            )}
            <Text className={"text-sm text-light/60"}>{t("common:click_to_view_answers")}</Text>
          </Stack>
        ),
        onClick: showDetailModalCallback
          ? () =>
              showDetailModalCallback(element, {
                answer_scale_option_ids: [option.id],
              })
          : undefined,
      });
    }
    return rowsInner;
  }, [result]);

  const rows2 = useMemo(() => {
    if (!scaleSettings || !result || !result.scale_data) return null;
    const scaleSecond = scaleSettings.form_scale.scale2?.sort((a, b) => a.sort - b.sort);
    if (!scaleSecond) return null;

    let sum = 0;
    for (const option of scaleSecond) {
      const selectOption = result.scale_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      sum += selectOption?.count || 0;
    }

    const rowsInner: DataBarsRow[] = [];
    for (const option of scaleSecond) {
      const selectOption = result.scale_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      const count = selectOption?.count || 0;
      const percent = sum > 0 ? Math.round((100 * count) / sum) : 0;
      rowsInner.push({
        title: option.title,
        emoji: option.emoji,
        value: count > 0 ? `${percent}%` : t("common:no_votes"),
        percent: percent,
        tooltip: (
          <Stack className={"text-sm"}>
            {count > 0 ? (
              <Text>{t("plurals:responses_count", { count: count })}</Text>
            ) : (
              <Text>{t("common:no_votes")}</Text>
            )}
            <Text className={"text-sm text-light/60"}>{t("common:click_to_view_answers")}</Text>
          </Stack>
        ),
        onClick: showDetailModalCallback
          ? () =>
              showDetailModalCallback(element, {
                answer_scale_option_ids: [option.id],
              })
          : undefined,
      });
    }
    return rowsInner;
  }, [result]);

  if (!scaleSettings) return null;

  return (
    <Box
      className={showDetailModalCallback ? "cursor-pointer" : undefined}
      onClick={showDetailModalCallback ? () => showDetailModalCallback(element) : undefined}
    >
      <Title
        header={scaleSettings.title || t("surveys:question_type_scale")}
        size={"sm"}
        className={scaleSettings.title ? "" : "text-secondary"}
        required={scaleSettings.is_required}
      />
      {result && result.scale_data && result.scale_data.comments_count > 0 && (
        <Box className={"py-4"}>
          <CommentsCount
            totalComments={result.scale_data.comments_count}
            commentersPreview={result.scale_data.commenters_preview || []}
          />
        </Box>
      )}
      {rows && <DataBars rows={rows} />}
      {rows2 && <DataBars rows={rows2} />}
    </Box>
  );
};
